/** MCEI requests and answers, always through the generated Object API. */
import * as flatbuffers from "flatbuffers";
import { BlobT } from "../../flatbuffers/generated/mce/blob";
import { BlobEncoding } from "../../flatbuffers/generated/mce/blob-encoding";
import { BlobKind } from "../../flatbuffers/generated/mce/blob-kind";
import { BuildHost } from "../../flatbuffers/generated/mce/build-host";
import { BuildInfoT } from "../../flatbuffers/generated/mce/build-info";
import { BuildRequestT } from "../../flatbuffers/generated/mce/build-request";
import { IdlBody } from "../../flatbuffers/generated/mce/idl-body";
import { IdlBundleT } from "../../flatbuffers/generated/mce/idl-bundle";
import { IdlMessage, IdlMessageT } from "../../flatbuffers/generated/mce/idl-message";
import { IdlResult, IdlResultT } from "../../flatbuffers/generated/mce/idl-result";
import { IdlSourceFileT } from "../../flatbuffers/generated/mce/idl-source-file";
import { InflateRequestT } from "../../flatbuffers/generated/mce/inflate-request";
import { Mapping, MappingT } from "../../flatbuffers/generated/mce/mapping";
import { MappingOverridesT } from "../../flatbuffers/generated/mce/mapping-overrides";
import { ProductType } from "../../flatbuffers/generated/mce/product-type";
import {
  TranslationTable,
  TranslationTableT,
} from "../../flatbuffers/generated/mce/translation-table";
import { ValidateRequestT } from "../../flatbuffers/generated/mce/validate-request";
import pin from "../../wasm/mce_idl/pin.json";

/** One file as the browser read it. */
export interface MceIdlUploadFile {
  name: string;
  bytes: Uint8Array;
}

/** The uploaded files, by the part each one plays. */
export interface MceIdlSources {
  main: MceIdlUploadFile;
  includes: MceIdlUploadFile[];
  controller?: MceIdlUploadFile;
  eventsXml?: MceIdlUploadFile;
}

const MCEI_INITIAL_BYTES = 1024;
const ARGS_VTABLE_OFFSET = 8;

function sourceFile(file: MceIdlUploadFile, kind: BlobKind): IdlSourceFileT {
  // A request may omit the sha and send Raw: the converter computes both.
  const content = new BlobT(kind, BlobEncoding.Raw, file.bytes.length, "", Array.from(file.bytes));
  return new IdlSourceFileT(file.name, content, false);
}

function bundleOf(sources: MceIdlSources): IdlBundleT {
  return new IdlBundleT(
    sourceFile(sources.main, BlobKind.IdlText),
    sources.includes.map((file) => sourceFile(file, BlobKind.IdlText)),
    sources.controller ? sourceFile(sources.controller, BlobKind.IdlText) : null,
    sources.eventsXml ? sourceFile(sources.eventsXml, BlobKind.EventsXml) : null,
  );
}

function packMessage(bodyType: IdlBody, body: IdlMessageT["body"]): Uint8Array {
  const builder = new flatbuffers.Builder(MCEI_INITIAL_BYTES);
  const message = new IdlMessageT(pin.abiVersion, bodyType, body);
  IdlMessage.finishIdlMessageBuffer(builder, message.pack(builder));
  return builder.asUint8Array();
}

/** An MCEI validate request. */
export function packValidateRequest(
  product: ProductType | null,
  sources: MceIdlSources,
): Uint8Array {
  return packMessage(IdlBody.ValidateRequest, new ValidateRequestT(product, bundleOf(sources)));
}

/** An MCEI build request. The browser build is a preview; LiftCloud stores its own rebuild. */
export function packBuildRequest(
  product: ProductType,
  sources: MceIdlSources,
  overrides: MappingOverridesT | null = null,
): Uint8Array {
  const info = new BuildInfoT(BuildHost.Browser, pin.converterCommit, pin.wasmSha256);
  return packMessage(
    IdlBody.BuildRequest,
    new BuildRequestT(product, bundleOf(sources), overrides, info),
  );
}

/** A build of the inputs a Mapping already holds. */
export function packBuildRequestFromMapping(
  mapping: MappingT,
  overrides: MappingOverridesT | null = null,
): Uint8Array {
  const info = new BuildInfoT(BuildHost.Browser, pin.converterCommit, pin.wasmSha256);
  return packMessage(
    IdlBody.BuildRequest,
    new BuildRequestT(mapping.productType, mapping.idl, overrides, info),
  );
}

/** An MCEI request to inflate one blob for display. */
export function packInflateRequest(blob: BlobT): Uint8Array {
  return packMessage(IdlBody.InflateRequest, new InflateRequestT(blob));
}

/** The IdlResult of an MCEI answer. Throws when the answer is not one. */
export function unpackResult(answer: Uint8Array): IdlResultT {
  const buffer = new flatbuffers.ByteBuffer(answer);
  if (!IdlMessage.bufferHasIdentifier(buffer)) {
    throw new Error("The converter's answer is not an MCEI message");
  }
  const message = IdlMessage.getRootAsIdlMessage(buffer).unpack();
  if (message.bodyType !== IdlBody.IdlResult || !(message.body instanceof IdlResultT)) {
    throw new Error("The converter's answer does not hold a result");
  }
  return message.body;
}

/** Whether a table holds the field at this vtable offset. */
function tableHasField(bb: flatbuffers.ByteBuffer, position: number, field: number): boolean {
  const vtable = position - bb.readInt32(position);
  return field < bb.readInt16(vtable) && bb.readInt16(vtable + field) !== 0;
}

/**
 * The commands whose signature the converter could not match. The Object API
 * turns absent args and an empty list into the same `[]`, so this reads the
 * buffer: an absent vector is an unmatchable signature, an empty one takes no
 * arguments. `args` is the table's third field, at vtable offset 8.
 */
export function unmatchableCommandNames(answer: Uint8Array): Set<string> {
  const message = IdlMessage.getRootAsIdlMessage(new flatbuffers.ByteBuffer(answer));
  const result = message.body(new IdlResult()) as IdlResult | null;
  const names = new Set<string>();
  for (let index = 0; index < (result?.commandsLength() ?? 0); index += 1) {
    const command = result?.commands(index);
    if (command?.bb && !tableHasField(command.bb, command.bb_pos, ARGS_VTABLE_OFFSET)) {
      names.add(asString(command.name()));
    }
  }
  return names;
}

/** The Mapping a build answered or an adapter stores. */
export function unpackMapping(cdoBinary: Uint8Array): MappingT {
  return Mapping.getRootAsMapping(new flatbuffers.ByteBuffer(cdoBinary)).unpack();
}

/** A translation blob, once inflated, is a size-prefixed MCET buffer. */
export function unpackTranslationTable(inflated: Uint8Array): TranslationTableT {
  const buffer = new flatbuffers.ByteBuffer(inflated);
  return TranslationTable.getSizePrefixedRootAsTranslationTable(buffer).unpack();
}

/** A generated string field as text. */
export function asString(value: string | Uint8Array | null | undefined): string {
  if (value == null) return "";
  return typeof value === "string" ? value : new TextDecoder().decode(value);
}
